import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useDateFilterVersion } from "@/lib/useDateFilterVersion";
import { X } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtQ = (n) => (!n ? "" : Number(n).toLocaleString("en-IN"));
const fmtV = (n) => (!n ? "" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtD = (iso) => {
  try {
    const d = new Date(iso + "T00:00:00");
    return `${d.getDate()}-${d.toLocaleString("en-GB", { month: "short" })}-${d.getFullYear()}`;
  } catch { return iso; }
};

export default function StockItemView({ onClose, initialItem }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const company = user?.company_name || "";
  const [items, setItems] = useState([]);
  const dfv = useDateFilterVersion();
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState(0);
  const [item, setItem] = useState("");        // level 2
  const [summary, setSummary] = useState(null);
  const [vouchers, setVouchers] = useState(null); // level 3

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/stock-items`);
        setItems(res.data.items || []);
      } catch {}
    })();
  }, [dfv]);

  const filtered = items.filter((n) => n.toLowerCase().includes(query.toLowerCase()));

  const openItem = useCallback(async (name) => {
    try {
      const res = await axios.get(`${API}/stock-item-summary`, { params: { item: name } });
      setItem(name); setSummary(res.data); setSel(0);
    } catch {}
  }, []);

  const openMonth = useCallback(async (m) => {
    try {
      const res = await axios.get(`${API}/stock-item-vouchers`, { params: { item, month: m } });
      setVouchers(res.data); setSel(0);
    } catch {}
  }, [item]);

  const openEntry = useCallback((r) => {
    if (!r || !r.src || !r.id) return;
    if (r.src === "sales") navigate(`/invoices/${r.id}/edit?return=inventory`);
    else if (r.src === "purchase") navigate(`/purchases?edit=${r.id}&return=inventory`);
  }, [navigate]);

  const back = useCallback(() => {
    if (vouchers) { setVouchers(null); setSel(0); }
    else if (item && !initialItem) { setItem(""); setSummary(null); setSel(0); }
    else onClose();
  }, [vouchers, item, initialItem, onClose]);

  useEffect(() => {
    if (initialItem) openItem(initialItem);
  }, [initialItem, openItem]);

  // Date filter changed while an item is open -> refresh that item in place.
  useEffect(() => {
    if (dfv && item) openItem(item);
  }, [dfv]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); back(); return; }
      const list = vouchers ? vouchers.rows : (summary ? summary.rows : filtered);
      if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, list.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
      else if (e.key === "Enter" && (tag !== "input" || !vouchers)) {
        e.preventDefault();
        if (vouchers) openEntry(vouchers.rows[sel]);
        else if (summary) { const r = summary.rows[sel]; if (r) openMonth(r.month); }
        else if (filtered[sel]) openItem(filtered[sel]);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [back, filtered, summary, vouchers, sel, openItem, openMonth, openEntry]);

  const Bar = ({ title }) => (
    <div className="flex items-center justify-between px-3 py-1.5 text-white" style={{ background: "#0f766e" }}>
      <span className="text-sm font-bold">{title}</span>
      <span className="text-sm font-black uppercase tracking-wide">{company}</span>
      <button onClick={back} data-testid="si-close" className="p-1 mr-12 hover:bg-white/20 rounded-sm">
        <X size={16} weight="bold" />
      </button>
    </div>
  );

  // ---------- Level 3: Stock Vouchers ----------
  if (vouchers) {
    const t = vouchers.totals;
    return (
      <div className="w-full" data-testid="stock-vouchers-view">
        <div className="w-full overflow-hidden shadow-xl flex flex-col" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3", height: "calc(100vh - 0.5rem)" }}>
          <Bar title="Stock Vouchers" />
          <div className="flex justify-between px-4 py-1 text-sm font-bold text-slate-900">
            <span>Stock Item: {vouchers.item}</span>
            <span data-testid="sv-period">{vouchers.label}</span>
          </div>
          <div className="flex items-end border-t border-b-2 border-slate-700 text-xs font-bold text-slate-800">
            <div className="w-[95px] px-2 py-1">Date</div>
            <div className="flex-1 px-2 py-1">Particulars</div>
            <div className="w-[90px] px-2 py-1 text-center">Vch Type</div>
            <div className="w-[90px] px-2 py-1 text-center">Vch No.</div>
            <div className="w-[170px] px-2 py-1 text-right">Inwards Qty · Value</div>
            <div className="w-[170px] px-2 py-1 text-right">Outwards Qty · Value</div>
            <div className="w-[170px] px-2 py-1 text-right">Closing Qty · Value</div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="sv-rows">
            <div className="flex items-center text-sm italic text-slate-700">
              <div className="w-[95px] px-2 py-0.5">{fmtD(vouchers.month + "-01")}</div>
              <div className="flex-1 px-2 py-0.5 font-bold">Opening Balance</div>
              <div className="w-[90px]" /><div className="w-[90px]" /><div className="w-[170px]" /><div className="w-[170px]" />
              <div className="w-[170px] px-2 py-0.5 text-right font-semibold">{fmtQ(vouchers.opening.q)} · {fmtV(vouchers.opening.v)}</div>
            </div>
            {vouchers.rows.map((r, i) => (
              <div key={i} onMouseEnter={() => setSel(i)} onClick={() => openEntry(r)}
                className={`flex items-center cursor-pointer text-sm ${sel === i ? "bg-teal-600 text-white" : "text-slate-900 hover:bg-[#f1e9d6]"}`}
                data-testid={`sv-row-${i}`}>
                <div className="w-[95px] px-2 py-0.5">{fmtD(r.date)}</div>
                <div className="flex-1 px-2 py-0.5 font-bold">{r.name}</div>
                <div className="w-[90px] px-2 py-0.5 text-center">{r.vch_type}</div>
                <div className="w-[90px] px-2 py-0.5 text-center">{r.vch_no}</div>
                <div className="w-[170px] px-2 py-0.5 text-right">{r.in_q ? `${fmtQ(r.in_q)} · ${fmtV(r.in_v)}` : ""}</div>
                <div className="w-[170px] px-2 py-0.5 text-right">{r.out_q ? `${fmtQ(r.out_q)} · ${fmtV(r.out_v)}` : ""}</div>
                <div className="w-[170px] px-2 py-0.5 text-right">{fmtQ(r.close_q)} · {fmtV(r.close_v)}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center border-t-2 border-slate-700 bg-[#f1e9d6] text-sm font-black text-slate-900" data-testid="sv-totals">
            <div className="flex-1 px-4 py-1.5">Totals</div>
            <div className="w-[170px] px-2 py-1.5 text-right">{fmtQ(t.in_q)} · {fmtV(t.in_v)}</div>
            <div className="w-[170px] px-2 py-1.5 text-right">{fmtQ(t.out_q)} · {fmtV(t.out_v)}</div>
            <div className="w-[170px] px-2 py-1.5 text-right">{fmtQ(t.close_q)} · {fmtV(t.close_v)}</div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Level 2: Monthly Summary ----------
  if (summary) {
    const gt = summary.grand_total;
    const maxOut = Math.max(...summary.rows.map((r) => Math.max(r.out_q, r.in_q)), 1);
    return (
      <div className="w-full" data-testid="stock-summary-view">
        <div className="w-full overflow-hidden shadow-xl flex flex-col" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3", height: "calc(100vh - 0.5rem)" }}>
          <Bar title="Stock Item Monthly Summary" />
          <div className="flex justify-between px-4 py-1 text-sm font-bold text-slate-900">
            <span>Particulars</span>
            <span className="italic" data-testid="ss-item">{summary.item}</span>
          </div>
          <div className="flex items-end border-t border-b-2 border-slate-700 text-xs font-bold text-slate-800">
            <div className="flex-1 px-3 py-1">Month</div>
            <div className="w-[200px] px-2 py-1 text-right">Inwards Qty · Value</div>
            <div className="w-[200px] px-2 py-1 text-right">Outwards Qty · Value</div>
            <div className="w-[220px] px-2 py-1 text-right">Closing Qty · Value</div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="ss-rows">
            {summary.rows.map((r, i) => (
              <div key={r.month} onMouseEnter={() => setSel(i)} onClick={() => openMonth(r.month)}
                className={`flex items-center cursor-pointer text-sm ${sel === i ? "bg-teal-600 text-white" : "text-slate-900 hover:bg-[#f1e9d6]"}`}
                data-testid={`ss-row-${r.label}`}>
                <div className="flex-1 px-3 py-0.5 font-bold">{r.label}</div>
                <div className="w-[200px] px-2 py-0.5 text-right">{r.in_q ? `${fmtQ(r.in_q)} · ${fmtV(r.in_v)}` : ""}</div>
                <div className="w-[200px] px-2 py-0.5 text-right">{r.out_q ? `${fmtQ(r.out_q)} · ${fmtV(r.out_v)}` : ""}</div>
                <div className="w-[220px] px-2 py-0.5 text-right font-semibold">{fmtQ(r.close_q)} · {fmtV(r.close_v)}</div>
              </div>
            ))}
          </div>
          {/* Movement graph — rounded teal gradient bars (in vs out) */}
          <div className="px-4 pt-2 pb-1 border-t border-slate-300" data-testid="ss-graph">
            <div className="flex items-end gap-1 h-24">
              {summary.rows.map((r) => (
                <div key={r.month} className="flex-1 flex items-end justify-center gap-0.5">
                  <div title={`In ${r.in_q}`} className="w-2.5 rounded-t-full"
                    style={{ height: `${(r.in_q / maxOut) * 88}px`, background: "linear-gradient(180deg,#14b8a6,#0f766e)" }} />
                  <div title={`Out ${r.out_q}`} className="w-2.5 rounded-t-full"
                    style={{ height: `${(r.out_q / maxOut) * 88}px`, background: "linear-gradient(180deg,#fbbf24,#d97706)" }} />
                </div>
              ))}
            </div>
            <div className="flex gap-1 text-[9px] font-bold text-slate-600">
              {summary.rows.map((r) => (
                <div key={r.month} className="flex-1 text-center">{r.label.slice(0, 3)}</div>
              ))}
            </div>
            <div className="flex gap-4 justify-center text-[10px] font-semibold text-slate-600 pb-1">
              <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: "#0f766e" }} />Inwards</span>
              <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: "#d97706" }} />Outwards</span>
            </div>
          </div>
          <div className="flex items-center border-t-2 border-slate-700 bg-[#f1e9d6] text-sm font-black text-slate-900" data-testid="ss-grand-total">
            <div className="flex-1 px-4 py-1.5 tracking-[0.2em]">Grand Total</div>
            <div className="w-[200px] px-2 py-1.5 text-right">{fmtQ(gt.in_q)} · {fmtV(gt.in_v)}</div>
            <div className="w-[200px] px-2 py-1.5 text-right">{fmtQ(gt.out_q)} · {fmtV(gt.out_v)}</div>
            <div className="w-[220px] px-2 py-1.5 text-right">{fmtQ(gt.close_q)} · {fmtV(gt.close_v)}</div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Level 1: Select Item ----------
  return (
    <div className="flex justify-center items-start pt-4 pb-10 min-h-[70vh]" data-testid="stock-item-select">
      <div className="w-full max-w-md rounded-md overflow-hidden shadow-xl" style={{ background: "#eaf6ea", border: "1px solid #cfe4cf" }}>
        <div className="text-white text-center py-2 text-sm font-bold tracking-widest uppercase" style={{ background: "#0f766e" }}>
          Select Item
        </div>
        <div className="px-6 pt-4 pb-2">
          <div className="text-center text-sm font-bold text-slate-900 underline mb-1">Name of Item</div>
          <input autoFocus value={query} onChange={(e) => { setQuery(e.target.value); setSel(0); }}
            data-testid="si-search"
            className="w-full px-3 py-1.5 border border-slate-400 rounded-sm text-sm bg-white"
            placeholder="Type to search…" />
        </div>
        <div className="text-white text-center py-1 text-xs font-bold tracking-widest uppercase" style={{ background: "#0f766e" }}>
          List of Stock Items
        </div>
        <div className="max-h-[52vh] overflow-y-auto py-1" data-testid="si-list">
          {filtered.map((n, i) => (
            <button key={n} onClick={() => openItem(n)} onMouseEnter={() => setSel(i)}
              data-testid={`si-item-${n}`}
              className={`w-full text-left px-4 py-0.5 text-sm font-semibold ${sel === i ? "bg-teal-600 text-white" : "text-slate-900 hover:bg-[#dcecdc]"}`}>
              {n}
            </button>
          ))}
          {filtered.length === 0 && <div className="px-4 py-3 text-xs text-slate-500 text-center">No stock items found</div>}
        </div>
        <div className="h-1.5" style={{ background: "#0f766e" }} />
      </div>
    </div>
  );
}
